import React from 'react';
import { motion } from 'framer-motion';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from './ui/accordion';

const faqs = [
  {
    id: 'faq-cost',
    question: 'How much does custom software cost?',
    answer: 'Custom software costs vary based on complexity, features, and timeline. Simple applications may start around $10,000, while enterprise solutions can range from $50,000 to $500,000+. We provide detailed estimates after understanding your specific requirements during our free consultation. Our transparent pricing means no hidden fees.',
  },
  {
    id: 'faq-timeline',
    question: 'How long does it take to build an app?',
    answer: 'Development timelines depend on the app\'s complexity. A basic MVP can be delivered in 2-3 months, while more complex applications may take 6-12 months. We use agile methodologies to deliver working features incrementally, so you\'ll see progress from week one.',
  },
  {
    id: 'faq-startups',
    question: 'Do you work with startups?',
    answer: 'Absolutely! We love working with startups and understand the unique challenges of bringing new ideas to market. We offer flexible engagement models and can help you build an MVP to validate your concept before scaling. Many of our long-term clients started as early-stage startups.',
  },
  {
    id: 'faq-technologies',
    question: 'What technologies do you use?',
    answer: 'We work with modern tech stacks including React, React Native, Node.js, Python, AWS, Google Cloud, and various AI/ML frameworks. We choose the best technologies based on your project requirements and long-term scalability needs. Our team stays current with the latest industry trends and best practices.',
  },
  {
    id: 'faq-support',
    question: 'Do you offer ongoing support?',
    answer: 'Yes, we offer comprehensive maintenance and support packages. This includes bug fixes, security updates, performance optimization, and feature enhancements to keep your software running smoothly. Our support team is available to help you whenever you need assistance.',
  },
];

const FAQSection = () => {
  return (
    <section
      id="faq"
      className="relative py-20 md:py-32 overflow-hidden"
      data-testid="faq-section"
    >
      {/* Background */}
      <div className="absolute inset-0 bg-[#030305]" />
      <div className="absolute inset-0 grid-bg opacity-20" />

      <div className="relative max-w-4xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12 md:mb-16"
        >
          <span className="text-cyan-400 text-sm font-mono tracking-wider uppercase mb-4 block">
            FAQ
          </span>
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-4" data-testid="faq-heading">
            Frequently Asked Questions
          </h2>
          <p className="text-gray-400 text-base md:text-lg max-w-2xl mx-auto">
            Got questions about custom software development in Miami? 
            Here are answers to the most common questions we receive.
          </p>
        </motion.div>

        {/* FAQ Accordion */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <Accordion type="single" collapsible className="w-full space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem
                key={faq.id}
                value={faq.id}
                className="bg-white/5 border border-white/10 rounded-xl px-6 hover:border-violet-500/30 transition-colors data-[state=open]:border-violet-500/50"
                data-testid={faq.id}
              >
                <AccordionTrigger className="text-white hover:no-underline py-5 text-left text-base md:text-lg font-medium [&[data-state=open]]:text-violet-400">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-gray-400 pb-5 text-sm md:text-base leading-relaxed">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </motion.div>

        {/* Additional CTA */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="text-center mt-12"
        >
          <p className="text-gray-500">
            Still have questions?{' '}
            <a 
              href="mailto:support@yaenlineasoft.com" 
              className="text-violet-400 hover:text-violet-300 transition-colors underline underline-offset-4"
              data-testid="faq-email-link"
            >
              Contact us directly
            </a>
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default FAQSection;
